import React, { useCallback, useEffect, useRef, useState } from 'react';
import '../../pages/Area/Area.css';
import './ConfirmDialog.css';

export interface ConfirmOptions {
  title: string;
  message: React.ReactNode;
  /** Подпись основной кнопки. */
  confirmText?: string;
  /** Подпись кнопки отмены; для информационного окна не показывается. */
  cancelText?: string;
  /** danger — красная основная кнопка (удаление и т.п.). */
  tone?: 'default' | 'danger';
  /** Акцентная строка над текстом — например, сумма списания. */
  highlight?: React.ReactNode;
}

interface DialogState extends ConfirmOptions {
  /** Информационное окно: одна кнопка, без отмены. */
  infoOnly: boolean;
  resolve: (value: boolean) => void;
}

/**
 * Окно подтверждения в стиле модалок кабинета вместо системных window.confirm / window.alert.
 *
 *   const { confirm, alert, dialog } = useConfirm();
 *   if (!(await confirm({ title: 'Оплатить камеру?', message: '…' }))) return;
 *   …
 *   return <>{dialog}…</>;
 */
export function useConfirm() {
  const [state, setState] = useState<DialogState | null>(null);

  const open = useCallback(
    (options: ConfirmOptions, infoOnly: boolean) =>
      new Promise<boolean>((resolve) => {
        setState({ ...options, infoOnly, resolve });
      }),
    []
  );

  const confirm = useCallback((options: ConfirmOptions) => open(options, false), [open]);
  const alert = useCallback(
    async (options: ConfirmOptions) => {
      await open(options, true);
    },
    [open]
  );

  const close = useCallback(
    (value: boolean) => {
      setState((current) => {
        current?.resolve(value);
        return null;
      });
    },
    []
  );

  const dialog = state ? <ConfirmDialogView state={state} onClose={close} /> : null;

  return { confirm, alert, dialog };
}

const ConfirmDialogView: React.FC<{ state: DialogState; onClose: (value: boolean) => void }> = ({
  state,
  onClose,
}) => {
  const confirmRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    confirmRef.current?.focus();
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [onClose]);

  const confirmClass =
    'area-modal__submit' + (state.tone === 'danger' ? ' area-modal__submit--danger' : '');

  return (
    <div className="area-modal confirm-dialog" role="alertdialog" aria-modal="true" aria-labelledby="confirm-dialog-title">
      <div className="area-modal__backdrop" onClick={() => onClose(false)} />
      <div className="area-modal__dialog area-modal__dialog--small confirm-dialog__box">
        <header className="area-modal__header">
          <h2 className="area-modal__title" id="confirm-dialog-title">
            {state.title}
          </h2>
          <button className="area-modal__close" type="button" onClick={() => onClose(false)} aria-label="Закрыть">
            ×
          </button>
        </header>

        <div className="area-modal__confirm-text">
          {state.highlight && <div className="confirm-dialog__highlight">{state.highlight}</div>}
          {state.message}
        </div>

        <footer className="area-modal__footer">
          {!state.infoOnly && (
            <button
              type="button"
              className="area-modal__submit area-modal__submit--secondary"
              onClick={() => onClose(false)}
            >
              {state.cancelText ?? 'Отмена'}
            </button>
          )}
          <button ref={confirmRef} type="button" className={confirmClass} onClick={() => onClose(true)}>
            {state.confirmText ?? (state.infoOnly ? 'Понятно' : 'Подтвердить')}
          </button>
        </footer>
      </div>
    </div>
  );
};
